import { useState } from 'react';
import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { formatDateTime } from '../../../../shared/format.js';
import { useDeviceLogs } from '../../hooks/useDeviceLogs.js';

function asArray(d) {
  return Array.isArray(d) ? d : d?.logs || d?.data || d?.items || [];
}

function PayloadCell({ payload }) {
  const [open, setOpen] = useState(false);
  if (!payload) return <span className="text-ink-3 text-xs">—</span>;
  const text = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
  return (
    <div>
      <button
        type="button"
        className="text-[11px] text-ink-3 hover:text-ink"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? 'إخفاء' : 'عرض JSON'}
      </button>
      {open ? (
        <pre className="mt-1 p-2 bg-paper-2 rounded-[8px] text-[11px] font-mono whitespace-pre-wrap max-w-[380px] overflow-x-auto">
          {text}
        </pre>
      ) : null}
    </div>
  );
}

export function DeviceLogsModal({ open, device, onClose }) {
  const [page, setPage] = useState(1);
  const id = device?._id || device?.id;
  const { data, isLoading, error } = useDeviceLogs(open ? id : null, { page, limit: 20 });

  if (!open) return null;

  const logs = asArray(data);
  const meta = data?.meta || data?.pagination || {};
  const totalPages = meta.totalPages || meta.pages || 1;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <div
        className="card w-[860px] max-w-full max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">
            سجل الجهاز: {device?.name || ''}
          </h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {error ? (
            <div className="text-red text-center py-8">تعذّر تحميل السجل</div>
          ) : isLoading ? (
            <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
          ) : logs.length === 0 ? (
            <div className="text-ink-3 text-center py-10 text-sm">لا توجد سجلات</div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="text-ink-2 text-[12px] sticky top-0 bg-white">
                <tr className="border-b border-line">
                  <th className="p-2.5 text-start">الوقت</th>
                  <th className="p-2.5 text-start">الحدث</th>
                  <th className="p-2.5 text-start">UID الكارت</th>
                  <th className="p-2.5 text-start">الحمولة</th>
                  <th className="p-2.5 text-start">النتيجة</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l._id || l.id} className="border-b border-line align-top">
                    <td className="p-2.5 font-mono text-[11.5px] text-ink-3 whitespace-nowrap">
                      {formatDateTime(l.createdAt || l.timestamp)}
                    </td>
                    <td className="p-2.5">{l.eventType || l.event || '—'}</td>
                    <td className="p-2.5 font-mono text-[12px]">{l.cardUid || '—'}</td>
                    <td className="p-2.5">
                      <PayloadCell payload={l.payload} />
                    </td>
                    <td className="p-2.5">
                      {l.success ? (
                        <Pill variant="success">نجح</Pill>
                      ) : (
                        <Pill variant="danger">فشل</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card-h border-b-0 border-t flex justify-between items-center">
          <div className="text-[12px] text-ink-3">
            الصفحة {page} من {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <Icon name="chevronR" size={14} /> السابق
            </button>
            <button
              type="button"
              className="btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              التالي <Icon name="chevronL" size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
